import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faChartColumn,
  faHistory,
  faExchangeAlt,
  faClock,
  faBook,
  faGraduationCap,
  faClipboardList,
  faLanguage,
  faPenToSquare,
  faChevronRight,
  faCalendarDay,
  faCheck,
  faCircle,
  faLayerGroup,
  faNewspaper,
  faCircleCheck,
} from '@fortawesome/free-solid-svg-icons';
import { ThemeContext } from "../../context/ThemeProvider";
import { BundleContext } from "../../context/BundleProvider";
import { AuthContext } from "../../context/AuthProvider";
import { APP_BUNDLES, GET_PRACTICE_TEST } from "../../../app_constant";
import { BUNDLE_ARTICLE_COUNTS } from "../../../content";
import { getTodayLocal } from "../../utils/dates";
import logger from "../../utils/logger";
import { IS_WHITE_LABEL } from "../../utils/brandFlags";
import { IMAGES } from "../../assets";

// Regular Menu Item Component
const RegularMenuItem = ({ item, styles, theme, hoveredItem, setHoveredItem, onClick, showDescription = true }) => {
  return (
    <motion.div
      variants={itemVariants}
      initial="inactive"
      animate={item.active ? "active" : "inactive"}
      whileHover="hover"
      className="relative group"
      onMouseEnter={() => setHoveredItem(item.path)}
      onMouseLeave={() => setHoveredItem(null)}
    >
      <motion.div
        variants={menuItemVariants}
        onClick={() => onClick(item.path)}
        className="cursor-pointer relative overflow-hidden rounded-xl mx-3 my-1 px-4 py-3.5 flex items-center transition-all duration-300"
        style={{
          background: item.active
            ? theme?.background?.blue
            : hoveredItem === item.path
            ? styles.card.background
            : 'transparent',
        }}
      >
        {/* Active indicator */}
        {item.active && (
          <motion.div
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            className="absolute left-0 top-0 bottom-0 w-0 rounded-r-full"
            style={{ backgroundColor: styles.primary.color }}
          />
        )}

        {/* Icon container */}
        <div className="relative z-10 w-10 h-10 flex items-center justify-center rounded-lg mr-2"
             style={{
               backgroundColor: item.active 
                 ? `${styles.primary.color}20`
                 : 'transparent'
             }}>
          <motion.div variants={iconVariants}>
            <FontAwesomeIcon
              icon={item.icon}
              className="text-lg transition-all duration-300"
              style={{
                color: item.active
                  ? styles.primary.color
                  : styles.text.primary,
              }}
            />
          </motion.div>
        </div>

        {/* Text content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold transition-colors duration-300 truncate"
             style={{
               color: item.active
                 ? styles.primary.color
                 : styles.text.primary
             }}>
            {item.name}
          </p>
          {showDescription && (
            <p className="text-xs mt-0.5 transition-colors duration-300 truncate"
               style={{
                 color: item.active
                   ? styles.primary.color
                   : styles.text.tertiary
               }}>
              {item.description}
            </p>
          )}
        </div>

        {/* Chevron for active items */}
        {item.active && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="ml-2"
          >
            <FontAwesomeIcon
              icon={faChevronRight}
              className="text-xs"
              style={{ color: styles.primary.color }}
            />
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

// Question of the Day Menu Item Component
const QuestionOfDayMenuItem = ({ item, styles, theme, hoveredItem, setHoveredItem, onClick, showDescription = true }) => {
  return (
    <motion.div
      variants={itemVariants}
      initial="inactive"
      animate={item.active ? "active" : "inactive"}
      whileHover="hover"
      className="relative group"
      onMouseEnter={() => setHoveredItem(item.path)}
      onMouseLeave={() => setHoveredItem(null)}
    >
      <motion.div
        variants={menuItemVariants}
        onClick={() => onClick(item.path)}
        className="cursor-pointer relative overflow-hidden rounded-xl mx-3 my-1 px-4 py-3.5 flex items-center transition-all duration-300 border-2 border-dashed"
        style={{
          borderColor: `${styles.primary.color}80`,
        }}
      >
        {/* Active indicator */}
        {item.active && (
          <motion.div
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            className="absolute left-0 top-0 bottom-0 w-0 rounded-r-full"
            style={{ backgroundColor: styles.primary.color }}
          />
        )}

        {/* Icon container */}
        <div className="relative z-10 w-10 h-10 flex items-center justify-center rounded-lg mr-2"
             style={{
               backgroundColor: item.hasAnswered 
                 ? `${styles.accent.success}30`
                 : `${styles.accent.error}30`
             }}>
          <motion.div variants={iconVariants}>
            <FontAwesomeIcon
              icon={item.hasAnswered ? faCircleCheck : faCircle}
              className="text-lg transition-all duration-300"
              style={{
                color: item.hasAnswered
                  ? styles.accent.success
                  : styles.accent.error,
              }}
            />
          </motion.div>
        </div>

        {/* Text content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold transition-colors duration-300 truncate"
             style={{
               color: item.active
                 ? styles.primary.color
                 : styles.text.primary
             }}>
            {item.name}
          </p>
          {showDescription && (
            <p className="text-xs mt-0.5 transition-colors duration-300 truncate"
               style={{
                 color: item.active
                   ? styles.primary.color
                   : styles.text.tertiary
               }}>
              {item.description}
            </p>
          )}
        </div>

        

        
      </motion.div>
    </motion.div>
  );
};

// Custom Test Menu Item Component
const CustomTestMenuItem = ({ item, styles, theme, hoveredItem, setHoveredItem, onClick, showDescription = true }) => {
  return (
    <motion.div
      variants={itemVariants}
      initial="inactive"
      animate={item.active ? "active" : "inactive"}
      whileHover="hover"
      className="relative group"
      onMouseEnter={() => setHoveredItem(item.path)}
      onMouseLeave={() => setHoveredItem(null)}
    >
      <motion.div
        variants={menuItemVariants}
        onClick={() => onClick(item.path)}
        className="cursor-pointer relative overflow-hidden rounded-xl mx-3 my-1 px-4 py-3.5 flex items-center transition-all duration-300 border-2 border-dashed"
        style={{
          background: theme?.background?.orange,
          borderColor: `${styles.accent.orange}40`,
          boxShadow: `0 8px 25px ${styles.accent.yellow}15, 0 4px 10px ${styles.accent.orange}10`
        }}
      >
        {/* Active indicator */}
        {item.active && (
          <motion.div
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            className="absolute left-0 top-0 bottom-0 w-0 rounded-r-full"
            style={{ backgroundColor: styles.primary.color }}
          />
        )}

        {/* Icon container */}
        <div className="relative z-10 w-10 h-10 flex items-center justify-center rounded-lg mr-2"
             style={{
               backgroundColor: `${styles.accent.orange}20`
             }}>
          <motion.div variants={iconVariants}>
            <FontAwesomeIcon
              icon={item.icon}
              className="text-lg transition-all duration-300"
              style={{
                color: styles.accent.orange
              }}
            />
          </motion.div>
        </div>

        {/* Text content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold transition-colors duration-300 truncate"
             style={{
               color: styles.accent.orange
             }}>
            {item.name}
          </p>
          {showDescription && (
            <p className="text-xs mt-0.5 transition-colors duration-300 truncate"
               style={{
                 color: `${styles.accent.orange}80`
               }}>
              {item.description}
            </p>
          )}
        </div>

        {/* Chevron for active items */}
        {item.active && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="ml-2"
          >
            <FontAwesomeIcon
              icon={faChevronRight}
              className="text-xs"
              style={{ color: styles.primary.color }}
            />
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

// Switch Exam Menu Item Component
const SwitchExamMenuItem = ({ item, styles, theme, hoveredItem, setHoveredItem, onClick, showDescription = true }) => {
  return (
    <motion.div
      variants={itemVariants}
      initial="inactive"
      animate={item.active ? "active" : "inactive"}
      whileHover="hover"
      className="relative group"
      onMouseEnter={() => setHoveredItem(item.name)}
      onMouseLeave={() => setHoveredItem(null)}
    >
      <motion.div
        variants={menuItemVariants}
        onClick={() => onClick()}
        className="cursor-pointer relative overflow-hidden rounded-xl mx-3 my-1 px-4 py-3.5 flex items-center transition-all duration-300"
        style={{
          background: theme?.primary?.blue?.[200]
        }}
      >
        {/* Icon container */}
        <div className="relative z-10 w-10 h-10 flex items-center justify-center rounded-lg mr-2"
             style={{
               backgroundColor: theme?.primary?.blue?.[500]
             }}>
          <motion.div variants={iconVariants}>
            <FontAwesomeIcon
              icon={item.icon}
              className="text-lg transition-all duration-300"
              style={{
                color: theme?.primary?.orange?.[500]
              }}
            />
          </motion.div>
        </div>

        {/* Text content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold transition-colors duration-300 truncate"
             style={{
               color: theme?.neutral?.black
             }}>
            {item.name}
          </p>
          {showDescription && (
            <p className="text-xs mt-0.5 transition-colors duration-300 truncate"
               style={{
                 color: theme?.neutral?.black
               }}>
              {item.description}
            </p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Animation variants (moved outside components for reuse)
const containerVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      staggerChildren: 0.05
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: { opacity: 1, x: 0 }
};

const menuItemVariants = {
  inactive: { 
    scale: 1,
    x: 0,
    transition: { duration: 0.2 }
  },
  active: { 
    scale: 1.02,
    x: 4,
    transition: { type: "spring", stiffness: 400, damping: 25 }
  },
  hover: { 
    scale: 1.02,
    x: 6,
    transition: { type: "spring", stiffness: 500, damping: 30 }
  }
};

const iconVariants = {
  inactive: { rotate: 0, scale: 1 },
  active: { rotate: 0, scale: 1.1 },
  hover: { rotate: 5, scale: 1.15 }
};

const Sidebar = ({ 
  isTestPage, 
  setIsTestExitPopup, 
  setNavigatePath, 
  setActive, 
  setLayoutState,
  setSwitchPopup,
  onExamSwitch
}) => {
  const { selectedCertificate } = useContext(BundleContext);
  const { theme, currentTheme } = useContext(ThemeContext);
  const { mySubscription, questionsList, articlesList, flashCardsList, acronymsList, glossaryList, qotd } = useContext(AuthContext);
  const [availableExams, setAvailableExams] = useState([]);
  const [daysLeft, setDaysLeft] = useState(null);
  const [logo, setLogo] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActiveLocal] = useState(location?.pathname);

  // Update active page state
  useEffect(() => {
    setActiveLocal(location?.pathname);
  }, [location.pathname]);

  // Load exams effect
  useEffect(() => {
    const loadExams = async () => {
      try {
        logger.log('Loading exams...');
        const exams = await APP_BUNDLES;
        logger.log('Available exams:', exams);
        logger.log('Number of exams:', exams.length);
        setAvailableExams(exams);
      } catch (error) {
        logger.error('Error loading exams:', error);
      }
    };
    loadExams();
  }, []);

  // Add effect to log when availableExams changes
  useEffect(() => {
    logger.log('availableExams state updated:', availableExams);
    logger.log('Number of exams in state:', availableExams.length);
  }, [availableExams]);

  // Calculate subscription days left
  useEffect(() => {
    if (mySubscription?.subscription_expiration) {
      const endDate = new Date(mySubscription.subscription_expiration.toDate());
      const today = new Date();
      const diffTime = endDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      setDaysLeft(diffDays > 0 ? diffDays : 0);
    } else {
      setDaysLeft(null);
    }
  }, [mySubscription]);

  // Define getAppLogo function outside useEffect so it can be reused
  const getAppLogo = async (currentTheme) => {
    // White-label brands show the partner logo (served from /assets/images by switch-app).
    if (IS_WHITE_LABEL) {
      return currentTheme === 'dark' ? IMAGES.client_logo_dark : IMAGES.client_logo_light;
    }

    if (currentTheme === 'light') {
        return IMAGES.logo_prepare;
    }
    else if (currentTheme === 'dark') {
        return IMAGES.logo_white;
    }
    else {
        return IMAGES.logo_prepare;
    }
  };

  useEffect(() => {
    getAppLogo(currentTheme || 'light').then(setLogo);
  }, [currentTheme]);

  // Calculate study tools stats similar to home component
  const studyToolsStats = {
    questions: questionsList?.length || 0,
    // Articles load lazily; use the static count so the sidebar shows it instantly.
    articles: articlesList?.length || BUNDLE_ARTICLE_COUNTS[selectedCertificate] || 0,
    practiceTests: GET_PRACTICE_TEST(selectedCertificate)?.length || 0,
    flashcards: flashCardsList?.length || 0,
    glossary: glossaryList?.length || 0,
    acronyms: acronymsList?.length || 0
  };

  // Define sidebar menu - Main Navigation
  const sidebarMenu = [
    {
      name: "Home",
      path: "/home",
      active: active === "/home",
      flag: true,
      icon: faHome,
      description: "Main dashboard"
    },
    {
      name: "Dashboard",
      path: "/dashboard",
      active: active === "/dashboard",
      flag: true,
      icon: faChartColumn,
      description: "Analytics & progress"
    },
    {
      name: "History",
      path: "/history",
      active: active === "/history",
      flag: true,
      icon: faHistory,
      description: "Past activities"
    },
  ];

  // Question of the Day menu item — check if user answered today's QOTD
  const [hasAnsweredToday, setHasAnsweredToday] = useState(false);
  useEffect(() => {
    const today = getTodayLocal();
    setHasAnsweredToday(!!qotd?.responses?.[today]);
  }, [qotd]);

  const questionOfDayMenu = [
    {
      name: "Question of the Day",
      path: "/qotd/answer",
      active: active === "/qotd/answer",
      flag: true,
      icon: faCalendarDay,
      description: "Daily practice question",
      isQuestionOfDay: true,
      hasAnswered: hasAnsweredToday
    }
  ];

  // Update the resourcesMenu to use feature flags and filter based on stats
  const resourcesMenu = [
    {
      name: "Glossary",
      path: "/glossary",
      active: active === "/glossary",
      flag: studyToolsStats.glossary > 0,
      icon: faBook,
      description: "Terms & definitions"
    },
    {
      name: "Acronyms",
      path: "/acronyms",
      active: active === "/acronyms",
      flag: studyToolsStats.acronyms > 0,
      icon: faLanguage,
      description: "Abbreviations list"
    },
  ];

  // Update the studyToolsMenu to use feature flags and filter based on stats
  const studyToolsMenu = [
    {
      name: "Study Questions",
      path: "/studyQuestion",
      active: active === "/studyQuestion",
      flag: studyToolsStats.questions > 0,
      icon: faGraduationCap,
      description: "Practice questions"
    },
    {
      name: "Practice Tests",
      path: "/practicetest",
      active: active === "/practicetest",
      flag: studyToolsStats.practiceTests > 0,
      icon: faClipboardList,
      description: "Full mock exams"
    },
    {
      name: "Flashcards",
      path: "/flashcard",
      active: active === "/flashcard",
      flag: studyToolsStats.flashcards > 0,
      icon: faLayerGroup,
      description: "Quick review cards"
    },
    {
      name: "Articles",
      path: "/articles",
      active: active === "/articles",
      flag: studyToolsStats.articles > 0,
      icon: faNewspaper,
      description: "Study Articles"
    }
  ];

  const customTestMenu = [
    {
      name: "Custom Test",
      path: "/customtest",
      active: active === "/customtest",
      flag: true,
      icon: faPenToSquare,
      description: "Build your own exam",
      isCustomTest: true // Special flag for custom styling
    }
  ];

  // Switch Exam menu item
  const switchExamMenu = [
    {
      name: "Switch Exam",
      path: null, // No path since it opens a dialog
      active: false,
      flag: availableExams.length > 1,
      icon: faExchangeAlt,
      description: "Change exam preparation",
      isAction: true // Special flag for action items
    }
  ];

  // Handle sidebar navigation
  const handleSidebarClick = (path) => {
    setNavigatePath(path);
    if (isTestPage && path !== "/test") {
      setIsTestExitPopup(true);
    } else {
      setIsTestExitPopup(false);
      setActiveLocal(path);
      setActive(path);
      navigate(path);
    }
  };

  // Handle switch exam click
  const handleSwitchExamClick = () => {
    if (location?.pathname === "/test") {
      setSwitchPopup(true);
    } else {
      onExamSwitch();
    }
  };

  // Theme-based styles using structured theme approach similar to exam.js
  const styles = {
    sidebar: {
      background: theme?.neutral?.white,
      backdropFilter: 'blur(10px)',
      borderRight: `1px solid ${theme?.primary?.gray?.[300]}`,
      boxShadow: '4px 0 20px rgba(0, 0, 0, 0.05)'
    },
    text: {
      primary: theme?.text?.primary,
      secondary: theme?.text?.secondary,
      tertiary: theme?.text?.tertiary
    },
    primary: {
      color: theme?.primary?.blue?.[500],
      light: theme?.primary?.blue?.[100],
      dark: theme?.primary?.blue?.[700]
    },
    accent: {
      success: theme?.primary?.green?.[500],
      warning: theme?.primary?.yellow?.[500],
      error: theme?.primary?.red?.[500],
      yellow: theme?.primary?.yellow?.[500],
      orange: theme?.primary?.orange?.[500]
    },
    card: {
      background: theme?.background?.blue
    }
  };

  // Helper function to render menu items based on type
  const renderMenuItem = (item, showDescription = true) => {
    if (!item.flag) return null;

    const commonProps = {
      item,
      styles,
      theme,
      hoveredItem,
      setHoveredItem,
      showDescription
    };

    if (item.isQuestionOfDay) {
      return (
        <QuestionOfDayMenuItem
          {...commonProps}
          onClick={handleSidebarClick}
        />
      );
    }

    if (item.isCustomTest) {
      return (
        <CustomTestMenuItem
          {...commonProps}
          onClick={handleSidebarClick}
        />
      );
    }

    if (item.isAction) {
      return (
        <SwitchExamMenuItem
          {...commonProps}
          onClick={handleSwitchExamClick}
        />
      );
    }

    // Default regular menu item
    return (
      <RegularMenuItem
        {...commonProps}
        onClick={handleSidebarClick}
      />
    );
  };

  const renderSectionHeader = (title) => (
    <motion.div variants={itemVariants} className="px-6 py-3">
      <h3 className="text-xs font-bold uppercase tracking-wider"
          style={{ color: styles.text.tertiary }}>
        {title}
      </h3>
    </motion.div>
  );

  return (
    <aside
      className="sidebar-container flex flex-row lg:flex-col fixed h-[70px] lg:h-full w-full lg:w-[240px] bottom-0 left-0 right-0 lg:top-0 lg:left-0 lg:bottom-0 z-40 transition-all duration-300"
      style={styles.sidebar}
    >
      
      {/* Desktop Sidebar Content */}
      <motion.div 
        className="hidden lg:flex lg:flex-col w-full h-full"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Logo Section */}
        <motion.div variants={itemVariants} className="flex items-center justify-center py-8 px-6">
          <div className="relative">
            <img
              className="h-8 w-auto max-w-full object-contain transition-all duration-300 hover:scale-105"
              src={logo}
              alt="logo"
            />
            <motion.div
              className="absolute -inset-2 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                background: `radial-gradient(circle, ${styles.primary.color}10 0%, transparent 70%)`
              }}
            />
          </div>
        </motion.div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto scrollbar-hide">
          {/* Main Navigation Section */}
          <motion.div variants={itemVariants} className="mb-6">
            {sidebarMenu.map((item) => (
              <React.Fragment key={item.path}>
                {renderMenuItem(item)}
              </React.Fragment>
            ))}
          </motion.div>

          {/* Question of the Day Section */}
          <motion.div variants={itemVariants} className="mb-6">
            {questionOfDayMenu.map((item) => (
              <React.Fragment key={item.path}>
                {renderMenuItem(item)}
              </React.Fragment>
            ))}
          </motion.div>

          {/* Study Tools Section */}
          <motion.div variants={itemVariants} className="mb-6">
            {renderSectionHeader("Study Tools")}
            {studyToolsMenu.map((item) => (
              <React.Fragment key={item.path}>
                {renderMenuItem(item)}
              </React.Fragment>
            ))}
          </motion.div>

          {/* Custom Test Section - Special styling */}
          <motion.div variants={itemVariants} className="mb-6">
            {customTestMenu.map((item) => (
              <React.Fragment key={item.path}>
                {renderMenuItem(item)}
              </React.Fragment>
            ))}
          </motion.div>

          {/* Resources Section */}
          <motion.div variants={itemVariants} className="mb-6">
            {renderSectionHeader("Resources")}
            {resourcesMenu.map((item) => (
              <React.Fragment key={item.path}>
                {renderMenuItem(item)}
              </React.Fragment>
            ))}
          </motion.div>
        </div>

        {/* Switch Exam Section - Sticky at bottom */}
        <motion.div variants={itemVariants} className="mt-auto mb-4">
          {switchExamMenu.map((item) => (
            <React.Fragment key={item.name}>
              {renderMenuItem(item)}
            </React.Fragment>
          ))}
        </motion.div>

        {/* Subscription Info - Enhanced */}
        {daysLeft !== null && (
          <motion.div 
            variants={itemVariants}
            className="mt-auto p-4 mx-3 mb-4 rounded-2xl border"
            style={{
              background: styles.card.background
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center"
                     style={{
                       backgroundColor: daysLeft > 7 ? `${styles.accent.success}20` : `${styles.accent.warning}20`
                     }}>
                  <FontAwesomeIcon 
                    icon={faClock} 
                    className="text-sm"
                    style={{
                      color: daysLeft > 7 ? styles.accent.success : styles.accent.warning
                    }}
                  />
                </div>
                <div>
                  <p className="text-xs font-medium" style={{ color: styles.text.tertiary }}>
                    {daysLeft > 0 ? "Access expires in" : "Access expired"}
                  </p>
                  {daysLeft > 0 && (
                    <p className="text-sm font-bold"
                       style={{ 
                         color: daysLeft > 7 ? styles.accent.success : styles.accent.warning
                       }}>
                      {daysLeft} days
                    </p>
                  )}
                </div>
              </div>
              {daysLeft > 0 && daysLeft <= 7 && (
                <div className="w-2 h-2 rounded-full animate-pulse"
                     style={{ backgroundColor: styles.accent.warning }} />
              )}
            </div>
          </motion.div>
        )}

        {/* White-label "Powered by LearnZapp" mark at the sidebar bottom. */}
        {IS_WHITE_LABEL && (
          <motion.div variants={itemVariants} className="flex flex-col items-center pb-4">
            <span className="text-[9px] leading-none mb-1" style={{ color: styles.text.tertiary }}>
              Powered by
            </span>
            <img src={IMAGES.logo} alt="Powered by LearnZapp" className="h-4 w-auto object-contain" />
          </motion.div>
        )}
      </motion.div>

      {/* Mobile Bottom Navigation - Enhanced */}
      <div className="flex lg:hidden flex-row w-full backdrop-blur-md"
           style={{
             background: styles.card.background
           }}>
        {sidebarMenu.map((item, idx) => (
          item.flag && (
            <motion.div
              key={idx}
              whileTap={{ scale: 0.95 }}
              whileHover={{ scale: 1.05 }}
              onClick={() => handleSidebarClick(item.path)}
              className="flex-1 h-full py-2 flex justify-center items-center flex-col relative"
            >
              {item.active && (
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  className="absolute top-0 left-1/2 transform -translate-x-1/2 h-0.5 w-8 rounded-full"
                  style={{ backgroundColor: styles.primary.color }}
                />
              )}
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center mb-1 transition-all duration-300 ${
                item.active ? 'shadow-lg' : ''
              }`}
                   style={{
                     backgroundColor: item.active ? `${styles.primary.color}20` : 'transparent',
                     boxShadow: item.active ? `0 4px 20px ${styles.primary.color}20` : 'none'
                   }}>
                <FontAwesomeIcon
                  icon={item.icon}
                  className="text-lg transition-all duration-300"
                  style={{
                    color: item.active
                      ? styles.primary.color
                      : styles.text.primary,
                    filter: item.active ? `drop-shadow(0 0 8px ${styles.primary.color}30)` : 'none'
                  }}
                />
              </div>
              <p className="text-[10px] font-medium text-center transition-colors duration-300"
                 style={{
                   color: item.active
                     ? styles.primary.color
                     : styles.text.primary
                 }}>
                {item.name}
              </p>
            </motion.div>
          )
        ))}

        {/* Question of the Day for Mobile */}
        {questionOfDayMenu.map((item, idx) => (
          item.flag && (
            <motion.div
              key={idx}
              whileTap={{ scale: 0.95 }}
              whileHover={{ scale: 1.05 }}
              onClick={() => handleSidebarClick(item.path)}
              className="flex-1 h-full py-2 flex justify-center items-center flex-col relative"
            >
              {item.active && (
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  className="absolute top-0 left-1/2 transform -translate-x-1/2 h-0.5 w-8 rounded-full"
                  style={{ backgroundColor: styles.primary.color }}
                />
              )}
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center mb-1 transition-all duration-300 relative ${
                item.active ? 'shadow-lg' : ''
              }`}
                   style={{
                     backgroundColor: item.active ? `${styles.primary.color}20` : 'transparent',
                     boxShadow: item.active ? `0 4px 20px ${styles.primary.color}20` : 'none'
                   }}>
                <FontAwesomeIcon
                  icon={item.icon}
                  className="text-lg transition-all duration-300"
                  style={{
                    color: item.hasAnswered
                      ? styles.accent.success
                      : styles.accent.error,
                    filter: `drop-shadow(0 0 8px ${
                      item.hasAnswered
                        ? styles.accent.success
                        : styles.accent.error
                    }30)`
                  }}
                />
                {/* Mobile status indicator */}
                <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full flex items-center justify-center"
                     style={{
                       backgroundColor: item.hasAnswered 
                         ? styles.accent.success 
                         : styles.accent.error
                     }}>
                  <FontAwesomeIcon
                    icon={item.hasAnswered ? faCheck : faCircle}
                    className="text-white text-xs"
                  />
                </div>
              </div>
              <p className="text-[10px] font-medium text-center transition-colors duration-300"
                 style={{
                   color: item.active
                     ? styles.primary.color
                     : styles.text.primary
                 }}>
                {item.name}
              </p>
            </motion.div>
          )
        ))}

        {/* Study Tools for Mobile - Only show items with content */}
        {studyToolsMenu.filter(item => item.flag).slice(0, 2).map((item, idx) => (
          <motion.div
            key={idx}
            whileTap={{ scale: 0.95 }}
            whileHover={{ scale: 1.05 }}
            onClick={() => handleSidebarClick(item.path)}
            className="flex-1 h-full py-2 flex justify-center items-center flex-col relative"
          >
            {item.active && (
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                className="absolute top-0 left-1/2 transform -translate-x-1/2 h-0.5 w-8 rounded-full"
                style={{ backgroundColor: styles.primary.color }}
              />
            )}
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center mb-1 transition-all duration-300 ${
              item.active ? 'shadow-lg' : ''
            }`}
                 style={{
                   backgroundColor: item.active ? `${styles.primary.color}20` : 'transparent',
                   boxShadow: item.active ? `0 4px 20px ${styles.primary.color}20` : 'none'
                 }}>
              <FontAwesomeIcon
                icon={item.icon}
                className="text-lg transition-all duration-300"
                style={{
                  color: item.active
                    ? styles.primary.color
                    : styles.text.primary,
                  filter: item.active ? `drop-shadow(0 0 8px ${styles.primary.color}30)` : 'none'
                }}
              />
            </div>
            <p className="text-[10px] font-medium text-center transition-colors duration-300"
               style={{
                 color: item.active
                   ? styles.primary.color
                   : styles.text.primary
               }}>
              {item.name}
            </p>
          </motion.div>
        ))}

        {/* Enhanced Switch Exam Button for Mobile */}
        {availableExams.length > 1 && (
          <motion.div
            whileTap={{ scale: 0.95 }}
            whileHover={{ scale: 1.05 }}
            onClick={handleSwitchExamClick}
            className="flex-1 h-full py-2 flex justify-center items-center flex-col"
          >
            <div className="w-8 h-8 rounded-xl flex items-center justify-center mb-1 shadow-lg"
                 style={{
                   background: `linear-gradient(135deg, ${styles.primary.color} 0%, ${styles.primary.dark} 100%)`
                 }}>
              <FontAwesomeIcon
                icon={faExchangeAlt}
                className="text-sm text-white"
              />
            </div>
            <p className="text-[10px] font-medium text-center" 
               style={{ color: styles.text.primary }}>
              Switch Exam
            </p>
          </motion.div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;